import { db } from '$lib/firebase';

import { collection, getDocs, setDoc, doc, 
    deleteDoc, updateDoc, type QueryDocumentSnapshot, getDoc } from "firebase/firestore";

import { getPictureURL } from '$lib/db/dishImages';
import { getOwnerByShopName } from '$lib/db/shop';
import { getCategories, indexCategory } from '$lib/db/categories';


let CACHE_MENU: { [key: string]: ShopMenu } = {};

export function invalidateMenuCache() {
    CACHE_MENU = {};
}

export async function addToMenu(shop: string, dish: Dish, categories: Categories): Promise<void> {
    const newOptions = [];

    for (const option of dish.options) {
        if (indexCategory(categories, dish.categoryID).sharedOptions.filter(o => o.name === option.name).length === 0) {
            option.name = option.name.trim();
            newOptions.push(option);
        }
    }

    await setDoc(doc(db, "shops", shop, "menu", dish.name.trim()), {
        description: dish.description.trim(),
        categoryID: dish.categoryID,
        price: dish.price,
        options: newOptions,
        selectedOptions: dish.selectedOptions,
        soldOut: false,
    });

    CACHE_MENU = {};
}

export async function sellOut(shopID: string, dish:Dish): Promise<void> {
    //update doc because dish must exist
    await updateDoc(doc(db, "shops", shopID, "menu", dish.name), {
        soldOut: true,
    });

    // Now that the db is updated, refresh cache.
    CACHE_MENU = {};
}

export async function unSellOut(shopID: string, dish:Dish): Promise<void> {
    //update doc because dish must exist
    await updateDoc(doc(db, "shops", shopID, "menu", dish.name), {
        soldOut: false,
    });

    CACHE_MENU = {};
}

async function convertDish(owner_uid:Promise<string>, snap: QueryDocumentSnapshot): Promise<Dish> {
    const data = snap.data();

    const dish:Dish = {
        name: snap.id,
        description: data.description ?? "",
        categoryID: data.categoryID ?? -1,
        price: data.price ?? 0,
        imageURL: getPictureURL(await owner_uid, snap.id),
        options: data.options ?? [],
        selectedOptions: data.selectedOptions ?? [],
        soldOut: data.soldOut ?? false,
        discountAmount: 0,
    }
    // console.log(dish.imageURL);
    return dish;
}


function getOptionsCategory(categories: Categories, categoryID: number): Option[] {
    if (categoryID === -1) {
        return [];
    }

    const category = categories[categoryID];

    if (!category || !category.sharedOptions) {
        return [];
    }

    return category.sharedOptions;
}


export async function getIndividualDish(shopID: string, dishName: string): Promise<Dish> {
    const ownerUID = getOwnerByShopName(shopID);

    const docRef = doc(db, "shops", shopID, "menu", dishName);

    const docSnap = await getDoc(docRef);
    // console.log(docRef.path);
    // console.log(docSnap.exists());

    if (!docSnap.exists()) {
        return generateDish();
    }

    const dish = await convertDish(ownerUID, docSnap);
    // console.log(dish);
    return dish;
}


export async function getMenu(shopID: string): Promise<ShopMenu> {
    if (typeof CACHE_MENU[shopID] !== "undefined") {
        return CACHE_MENU[shopID];
    };

    // get a promise of the owner uid
    const ownerUID = getOwnerByShopName(shopID);

    const menuCol = collection(db, "shops", shopID, 'menu');
    const menuSnapshot = await getDocs(menuCol);

    const menu:Array<Promise<Dish>> = [];

    const categories: Categories = await getCategories(shopID);

    for (const doc of menuSnapshot.docs) {
        // fulfill promise inside convertDish and get dish
        const dish = convertDish(ownerUID, doc);         

        menu.push(dish);
    }

    const menuResolved: ShopMenu = await Promise.all(menu);

    for (const dish of menuResolved) {
        const defaultOptions = getOptionsCategory(categories, dish.categoryID);
        // console.log(defaultOptions)

        // add default options
        dish.options = [...dish.options, ...defaultOptions];
    }

    CACHE_MENU[shopID] = menuResolved;
    
    // console.log(menuResolved);
    return menuResolved;
}


export function getIndividualPrice(dish: Dish): number
{
    let itemPrice = dish.price;

    for (let i=0; i < dish.options.length; i++) {
        if (dish.selectedOptions[i]) {
            itemPrice += dish.options[i].price;
        }
    }
    
    return itemPrice;
}


export function findDishNameMenu(dishName: string, menu: ShopMenu): Dish {
    let dish = generateDish();

    for (const dishInMenu of menu) {
        if (dishInMenu.name === dishName) {
            dish = {...dishInMenu};
        }
    }
    
    dish.selectedOptions = new Array(dish.options.length).fill(false);
    return dish;
}

export async function deleteDishFromMenu(shop:string, name:string): Promise<void> {
    try {
        const docRef = doc(db, "shops", shop, "menu", name);
        await deleteDoc(docRef);

    } catch (e) {
        console.log(e);
    }

    CACHE_MENU = {};
}


export function sortMenuIntoCategories(menu: ShopMenu): CategoryMenu {
    const sortedDishes:CategoryMenu = {};

    for (const dish of menu) {
        if (!sortedDishes[dish.categoryID]) {
            sortedDishes[dish.categoryID] = [];
        }

        sortedDishes[dish.categoryID].push(dish);
    }

    return sortedDishes;
};


export async function updateMenuCategories(shop: string, deleteCategories: number[]) {    
    const menuColl = collection(db, "shops", shop, 'menu');
    const menuSnapshot = await getDocs(menuColl);    

    for (const doc of menuSnapshot.docs) {
        const dishData = doc.data();

        if (deleteCategories.includes(dishData?.categoryID)) {
            dishData.categoryID = -1;
            await updateDoc(doc.ref, dishData);
        }
    }
}

export function generateDish(): Dish {
    const dish:Dish = {
        name: "",
        description: "",
        categoryID: -1,
        price: 0,
        imageURL: "",
        options: [],
        selectedOptions: [],
        soldOut: false,
        discountAmount: 0
    }

    return dish;
}

export function generateOption(): Option {
    return {
        name: "",
        price: 0,
        discountAmount: 0,
        optionCategory: "",
    }
}
